import { humaneDuration } from '@nightshift/ui-duration';

/**
 * Formatting.
 *
 * Two rules run through all of it, both from the design brief:
 *
 *  - **Relative on cards, absolute on hover.** "38m ago" is what the operator needs at a
 *    glance; the exact clock time is what they need when they are reconstructing an incident.
 *    Every relative string here has an absolute counterpart meant for a `title`.
 *  - **Never fabricate a number.** A count nobody reported renders as an em dash, never as a
 *    confident zero — `tokens(0)` is a dash for the same reason. Flattening "unknown" into
 *    "none" would invent a fact in the only place anyone looks.
 */

/**
 * `44s`, `9m 39s`, `2h 12m` — one shape for every elapsed span in a table.
 *
 * Every part is unit-suffixed, deliberately. This function used to render a sub-hour span in
 * clock form, so a single Runs column could hold "7s", "17:00", "9:39" and "2h 2m" at once
 * and the reader had to decode each cell: is "17:00" seventeen minutes, or five o'clock? A
 * duration and a time of day must never be spelled the same way in a product that shows both.
 */
export function compactDuration(ms: number): string {
  return humaneDuration(ms);
}

/** `38m ago`, `2d ago`, `just now`. */
export function relativeTime(at: number, now = Date.now()): string {
  const delta = now - at;
  if (!Number.isFinite(delta)) return '—';
  if (delta < 0) return 'in a moment';
  const seconds = Math.floor(delta / 1000);
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

/** `38m`, `2d` — the same thing without "ago", for a badge on a card. */
export function relativeShort(at: number, now = Date.now()): string {
  const delta = Math.max(0, now - at);
  const seconds = Math.floor(delta / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** `in 28s` — the shape a backoff or deferral countdown wants. */
/**
 * A countdown at the precision a reader can use: seconds under a minute, minutes under an
 * hour, hours and minutes under a day, then days and hours. "163h 56m" is a sum nobody does
 * in their head; "6d 19h" is the same wait said the way people say it.
 */
export function untilShort(at: number, now = Date.now()): string {
  const delta = at - now;
  if (delta <= 0) return 'now';
  const seconds = Math.ceil(delta / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
  }
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours === 0 ? `${days}d` : `${days}d ${restHours}h`;
}

function upperMeridiem(value: string): string {
  return value.replace(/\b(am|pm)\b/gi, (token) => token.toUpperCase());
}

export function clockTime(at: number, timeZone?: string): string {
  return upperMeridiem(new Date(at).toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  }));
}

/**
 * An absolute time carries its date unless it is from today.
 *
 * Trace timelines, the drawer Audit table and the Runs table used to print `HH:MM` only, so
 * a six-day-old ticket that crossed midnight read as 23:11 → 00:11 with the date only in a
 * hover. The morning-after check has to answer "when" without a mouse.
 */
export function clockStamp(at: number, now = Date.now()): string {
  const time = clockTime(at);
  const when = new Date(at);
  const today = new Date(now);
  if (when.toDateString() === today.toDateString()) return time;
  const day = when.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  return `${day} ${time}`;
}

export function clockStampSeconds(at: number, now = Date.now()): string {
  const time = clockSeconds(at);
  const when = new Date(at);
  const today = new Date(now);
  if (when.toDateString() === today.toDateString()) return time;
  const day = when.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  return `${day} ${time}`;
}

export const TIME_FORMATS = ['relative', 'local'] as const;
export type TimeFormat = (typeof TIME_FORMATS)[number];

export function isTimeFormat(value: string | null | undefined): value is TimeFormat {
  return value === 'relative' || value === 'local';
}

/** Age, or the operator's local clock time — Settings → Display chooses. */
export function observedTime(at: number, now = Date.now(), format: TimeFormat = 'relative'): string {
  return format === 'local' ? clockStamp(at, now) : relativeTime(at, now);
}

export function clockSeconds(at: number): string {
  return upperMeridiem(new Date(at).toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }));
}

/** The absolute form that belongs in a `title` next to any relative string. */
export function absoluteTime(at: number): string {
  return upperMeridiem(new Date(at).toLocaleString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }));
}

/** `3 Oct 2026`: a day, for things that happen on a day rather than at a minute. */
export function calendarDate(at: number): string {
  return new Date(at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** `4.1M`, `912k`, `418` — token counts get long fast. */
export function tokens(count: number): string {
  if (!Number.isFinite(count) || count <= 0) return '—';
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${Math.round(count / 1_000)}k`;
  return String(count);
}

export function bytes(count: number): string {
  if (count >= 1024 ** 3) return `${(count / 1024 ** 3).toFixed(1)} GB`;
  if (count >= 1024 * 1024) return `${(count / (1024 * 1024)).toFixed(1)} MB`;
  if (count >= 1024) return `${Math.round(count / 1024)} kB`;
  return `${count} B`;
}

/** Known US-dollar spend; callers decide what an unknown value means in their context. */
export function usd(amount: number): string {
  if (!Number.isFinite(amount) || amount < 0) return '—';
  if (amount === 0) return '$0.00';
  if (amount < 0.01) return '<$0.01';
  return `$${amount.toFixed(2)}`;
}

export function plural(count: number, singular: string, plural_?: string): string {
  return count === 1 ? `${count} ${singular}` : `${count} ${plural_ ?? `${singular}s`}`;
}

/**
 * A GitLab pipeline or job status in words.
 *
 * The host's tokens are snake_case, and the board printed them raw: a job held behind a
 * resource group read `waiting_for_resource`, which looks like a leaked field name and is the
 * longest string that column is ever asked to hold. Only the underscores go -- GitLab's own
 * vocabulary is what the pipeline page will show the operator next, so nothing is renamed
 * here, and a status the host adds later still arrives as words.
 */
export function ciStatusWords(status: string): string {
  const token = status.trim().toLowerCase();
  return token.length === 0 ? '—' : token.replace(/_/g, ' ');
}
